import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import { Box, Typography, Button, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { showToast } from "../../../utils/toast";
import { verifyEmailAPI, checkUserByEmailAPI } from "../../../api/userApi";
import { trackEmailClicked } from "../../../analytics/canonicalTracking";
import { isStagingCreatorOnboardingBypassEnabled } from "../../../config/stagingCreatorOnboarding";

const useStyles = makeStyles(theme => ({
  container: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#121212",
    padding: 20,
  },
  paper: {
    padding: 40,
    maxWidth: 500,
    width: "100%",
    backgroundColor: "#1e1e1e",
    color: "#fff",
    textAlign: "center" as const,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    borderRadius: 12,
  },
  bigIcon: {
    fontSize: 60,
    marginBottom: 20,
  },
  title: {
    marginBottom: 20,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  subtitle: {
    marginBottom: 30,
    color: "#BDBDBD",
  },
  email: {
    marginBottom: 30,
    color: theme.palette.primary.main,
    wordBreak: "break-all" as const,
    fontWeight: 600,
  },
  infoBox: {
    backgroundColor: "#262626",
    padding: 20,
    borderRadius: 8,
    marginBottom: 30,
    textAlign: "left" as const,
  },
  warnBox: {
    backgroundColor: "rgba(255, 152, 0, 0.12)",
    padding: 15,
    borderRadius: 8,
    marginBottom: 30,
    border: "1px solid #FF9800",
  },
  warnText: {
    color: "#FFB74D",
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 15,
  },
  primaryBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "12px 24px",
    "&:hover": { backgroundColor: "#d95aab" },
  },
  outlineSuccessBtn: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    padding: "12px 24px",
    "&.Mui-disabled": {
      color: "#ffffff",
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  textBtn: {
    color: "#9E9E9E",
    padding: "8px 16px",
  },
}));

const EmailActivationPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const seo = useSEO("email-activation");
  const classes = useStyles();

  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(60);
  const [isResending, setIsResending] = useState<boolean>(false);
  const emailClickTracked = useRef(false);

  // Get user data from location state or localStorage
  const userData =
    (location.state as any)?.userData ||
    (localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user")!)
      : null);

  // Get token and success status from URL query params (for email verification link)
  const tokenFromUrl = new URLSearchParams(location.search).get("token");
  const successParam = new URLSearchParams(location.search).get("success");

  useEffect(() => {
    if (emailClickTracked.current) return;
    if (successParam === "true" && tokenFromUrl) {
      emailClickTracked.current = true;
      trackEmailClicked({
        email_type: "activation",
        user_id: userData?.id,
        destination_type: "app_email_activation",
      });
    }
  }, [successParam, tokenFromUrl, userData?.id]);

  // Check if we have user data or token from URL
  const hasUserData = userData || tokenFromUrl;

  // Staging: skip waiting for email link (backend should auto-validate).
  useEffect(() => {
    if (!isStagingCreatorOnboardingBypassEnabled()) {
      return;
    }
    history.replace("/");
  }, [history]);

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Periodically check email verification status
  useEffect(() => {
    // Перевіряємо статус тільки якщо є email і email ще не верифіковано
    if (!userData?.email || isEmailVerified || showSuccessMessage) {
      return;
    }

    const checkVerificationStatus = async () => {
      try {
        const response = await checkUserByEmailAPI(userData.email);

        // Перевіряємо структуру відповіді: response.user.validated або response.validated
        const isValidated =
          response?.data?.user?.validated === true ||
          response?.validated === true;

        if (isValidated) {
          setIsEmailVerified(true);
          setShowSuccessMessage(true);
          showToast(
            "Email verified successfully! Redirecting to login...",
            "success"
          );

          // Redirect to login page after 3 seconds
          setTimeout(() => {
            history.push("/");
          }, 3000);
        }
      } catch (error) {
        // Тихо обробляємо помилки, щоб не заважати користувачу
        console.log("Checking verification status...");
      }
    };

    // Перевіряємо одразу
    checkVerificationStatus();

    // Перевіряємо кожні 5 секунд
    const interval = setInterval(checkVerificationStatus, 5000);

    return () => clearInterval(interval);
  }, [userData?.email, isEmailVerified, showSuccessMessage, history]);

  // Verify email if token is present in URL
  useEffect(() => {
    const verifyEmailWithToken = async () => {
      // Якщо success=true, значить email вже верифіковано через GET запит на бекенді
      if (successParam === "true" && tokenFromUrl) {
        setIsEmailVerified(true);
        setShowSuccessMessage(true);
        showToast(
          "Email verified successfully! Redirecting to login...",
          "success"
        );

        // Redirect to login page after 3 seconds
        setTimeout(() => {
          history.push("/");
        }, 3000);
        return;
      }

      // Якщо є токен, але немає success=true, виконуємо GET запит для автоматичної верифікації
      if (tokenFromUrl && !isEmailVerified && successParam !== "true") {
        // Використовуємо window.location для переходу на GET endpoint
        // Бекенд автоматично перенаправить на /email-activation?success=true&token=...
        // Браузер обробить редирект, і React Router оновить компонент з новими query параметрами
        const baseUrl =
          process.env.REACT_APP_SERVER_URL || window.location.origin;
        const verifyUrl = `${baseUrl}/api/user/verify-email?token=${encodeURIComponent(
          tokenFromUrl
        )}`;

        // Переходимо на GET endpoint - бекенд обробить верифікацію і зробить редирект
        window.location.href = verifyUrl;
      }
    };

    verifyEmailWithToken();
  }, [tokenFromUrl, successParam, isEmailVerified, history]);

  const handleResendEmail = async () => {
    if (resendCooldown > 0 || isResending) return;

    if (!userData?.email) {
      showToast("User email not available. Please register again.", "error");
      return;
    }

    setIsResending(true);
    try {
      // Викликаємо API для повторної відправки email верифікації
      // Припускаємо, що бекенд має endpoint для цього
      // Якщо такого немає, можна використати forgot-password або створити новий endpoint
      showToast(
        "Please use the 'Forgot Password' feature or contact support to resend verification email.",
        "info"
      );
      setResendCooldown(60); // Reset cooldown to 60 seconds
    } catch (error) {
      console.error("Error sending verification email:", error);
      showToast("Failed to send activation email. Please try again.", "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckEmail = async () => {
    // Якщо є token в URL, спробуємо верифікувати
    if (tokenFromUrl) {
      try {
        const response = await verifyEmailAPI(tokenFromUrl);

        if (response.success) {
          setIsEmailVerified(true);
          setShowSuccessMessage(true);
          showToast(
            "Email verified successfully! Redirecting...",
            "success"
          );
          setTimeout(() => {
            history.push("/");
          }, 2000);
        } else {
          showToast(
            response.message ||
              "Email not yet verified. Please check your inbox.",
            "info"
          );
        }
      } catch (error: any) {
        console.error("Error checking email verification:", error);
        const errorMessage =
          error?.response?.data?.message ||
          "Email not yet verified. Please check your inbox.";
        showToast(errorMessage, "info");
      }
    } else {
      showToast("Please click the verification link in your email.", "info");
    }
  };

  const handleGoToLogin = () => {
    history.push("/login");
  };

  // Redirect if no user data available
  if (!hasUserData) {
    return <Redirect to="/login" />;
  }

  return (
    <>
      <StandardPageHelmet seo={seo} />

      <Box className={classes.container}>
        <Paper className={classes.paper}>
          {showSuccessMessage ? (
            <>
              <div className={classes.bigIcon}>✅</div>
              <Typography variant="h4" className={classes.title}>
                Email Verified Successfully
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                Your email has been verified. You will be redirected to the
                login page in a few seconds.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoToLogin}
                className={classes.primaryBtn}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <div className={classes.bigIcon}>📧</div>
              <Typography variant="h4" className={classes.title}>
                Check Your Email
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                We've sent an activation email to:
              </Typography>
              <Typography variant="h6" className={classes.email}>
                {userData?.email || "Email not available"}
              </Typography>

              <div className={classes.infoBox}>
                <Typography variant="body2" style={{ marginBottom: "10px" }}>
                  <strong>Next steps:</strong>
                </Typography>
                <Typography variant="body2" style={{ marginBottom: "5px" }}>
                  1. Check your email inbox (and spam folder)
                </Typography>
                <Typography variant="body2" style={{ marginBottom: "5px" }}>
                  2. Click the activation link in the email
                </Typography>
                <Typography variant="body2">
                  3. Return here to complete the process
                </Typography>
              </div>

              <div className={classes.warnBox}>
                <Typography variant="body2" className={classes.warnText}>
                  <strong>Important:</strong> You must verify your email to
                  access your account.
                </Typography>
              </div>

              <Box className={classes.actions}>
                <Button
                  variant="contained"
                  onClick={handleCheckEmail}
                  className={classes.primaryBtn}
                >
                  I've Verified My Email
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleResendEmail}
                  disabled={resendCooldown > 0 || isResending}
                  className={classes.outlineSuccessBtn}
                >
                  {isResending
                    ? "Sending..."
                    : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend Activation Email"}
                </Button>

                <Button
                  variant="text"
                  onClick={handleGoToLogin}
                  className={classes.textBtn}
                >
                  Back to Login
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default EmailActivationPage;
